import * as StellarSdk from '@stellar/stellar-sdk';
import BigNumber from 'bignumber.js';

import { getEnvClassicAssetData } from 'constants/assets-env';
import { PROPOSAL_STATUS } from 'constants/dao';
import { TIMEFRAMES } from 'constants/intervals';
import { MAX_OPERATIONS_PER_TRANSACTION } from 'constants/stellar';

import {
    LogVote,
    Proposal,
    ProposalSimple,
    ProposalVoteClaim,
    VoteChoiceSimple,
} from 'types/governance';

export const getQuorumPercentage = (proposal: ProposalSimple | Proposal) => {
    const {
        vote_for_result: voteFor,
        vote_against_result: voteAgainst,
        vote_abstain_result: voteAbstain,
        aqua_circulating_supply: aquaCirculatingSupply,
        ice_circulating_supply: iceCirculatingSupply,
    } = proposal;

    const voteForValue = Number(voteFor);
    const voteAgainstValue = Number(voteAgainst);
    const voteAbstainValue = Number(voteAbstain);

    return (
        ((voteForValue + voteAgainstValue + voteAbstainValue) /
            (Number(aquaCirculatingSupply) + Number(iceCirculatingSupply))) *
        100
    );
};

export const isQuorumReached = (proposal: ProposalSimple | Proposal) => {
    const percentForQuorum = Number(proposal.percent_for_quorum);

    return getQuorumPercentage(proposal) >= percentForQuorum;
};

export const getProposalStatus = (proposal: ProposalSimple | Proposal): PROPOSAL_STATUS => {
    if (proposal.proposal_status === 'DISCUSSION') return PROPOSAL_STATUS.DISCUSSION;
    if (proposal.proposal_status === 'QUEUED') {
        return PROPOSAL_STATUS.QUEUE;
    }
    if (proposal.proposal_status === 'VOTING') return PROPOSAL_STATUS.ACTIVE;
    if (proposal.proposal_status === 'EXPIRED') return PROPOSAL_STATUS.EXPIRED;

    if (!isQuorumReached(proposal)) {
        return PROPOSAL_STATUS.NO_QUORUM;
    }

    return Number(proposal.vote_for_result) > Number(proposal.vote_against_result)
        ? PROPOSAL_STATUS.ACCEPTED
        : PROPOSAL_STATUS.REJECTED;
};

export const isProposalQueued = (proposal: ProposalSimple | Proposal) =>
    proposal.proposal_status === 'QUEUED';

export const getVotingTokens = (proposal: ProposalSimple | Proposal) => {
    if (!Number(proposal.aqua_circulating_supply)) return 'ICE';
    if (!Number(proposal.ice_circulating_supply)) return 'AQUA';

    return 'AQUA + ICE';
};

// Vote claimable balances become claimable by the voter one hour after the voting ends.
export const getGovernanceVoteUnlockTimestamp = (endAt: string): number =>
    new Date(endAt).getTime() + TIMEFRAMES.HOUR;

// The governance backend counts a vote whose unlock time is within this many seconds of the
// expected one.
const UNLOCK_TIMESTAMP_TOLERANCE_SECONDS = 1;

// The ICE approval server wraps the transaction with an open and a close trustline flags
// operation for every ICE asset in it.
const ICE_APPROVAL_OPERATIONS_PER_ASSET = 2;

// governICE and gdICE vote balances are clawed back and recreated with the same claimants and
// a new balance id, so the balance id stored with a vote goes stale. AQUA vote balances keep
// their id until they are claimed. Claiming these assets also needs the ICE approval flow.
export const getReissuedVoteAssetCodes = (): string[] => [
    getEnvClassicAssetData('governIce').code,
    getEnvClassicAssetData('gdIce').code,
];

// A vote balance is claimable by the voter only through `not(abs_before <date>)`, which is what
// a vote created by this app carries. Returns that date, or null for any other predicate.
export const getVoteClaimBackDate = (
    predicate: StellarSdk.Horizon.HorizonApi.Predicate | undefined,
): string | null => {
    if (!predicate?.not || Object.keys(predicate).length !== 1) {
        return null;
    }

    const { abs_before: absBefore, ...rest } = predicate.not as Record<string, unknown>;
    const hasOtherConditions = Object.keys(rest).some(key => key !== 'abs_before_epoch');

    if (
        hasOtherConditions ||
        typeof absBefore !== 'string' ||
        !Number.isFinite(new Date(absBefore).getTime())
    ) {
        return null;
    }

    return absBefore;
};

export type VoteClaimBalance = Pick<
    ProposalVoteClaim,
    'id' | 'amount' | 'assetCode' | 'voteChoice' | 'claimBackDate'
>;

export type VoteClaimGroup<T extends VoteClaimBalance = VoteClaimBalance> = {
    key: string;
    voteChoice: VoteChoiceSimple;
    assetCode: string;
    // Live claimable balances of the group; empty when everything in the group is claimed.
    balances: T[];
    // Vote rows from the governance API that belong to the group; empty for live balances that
    // have no counted vote.
    logs: LogVote[];
    // Sum of the live balances, i.e. what claiming the group returns.
    amount: string;
    unlockTimestamp: number | null;
};

export type VoteClaimRow = {
    log: LogVote;
    groupKey: string;
};

export enum VoteClaimStatus {
    claimed = 'claimed',
    locked = 'locked',
    claimable = 'claimable',
}

type VoteClaimGroupKeyParams = {
    voteChoice: VoteChoiceSimple;
    assetCode: string;
    balanceId: string;
    unlockTimestamp: number | null;
    reissuedAssetCodes: string[];
};

// Balances of reissued assets are grouped by vote choice, asset and unlock time, because that is
// all that survives a reissue. AQUA balances are keyed by their own id.
const getVoteClaimGroupKey = ({
    voteChoice,
    assetCode,
    balanceId,
    unlockTimestamp,
    reissuedAssetCodes,
}: VoteClaimGroupKeyParams): string => {
    if (!reissuedAssetCodes.includes(assetCode)) {
        return `${voteChoice}|${assetCode}|id:${balanceId}`;
    }

    const unlockSeconds = Number.isFinite(unlockTimestamp)
        ? Math.ceil(unlockTimestamp / 1000)
        : 'unknown';

    return `${voteChoice}|${assetCode}|${unlockSeconds}`;
};

// Unlock seconds to try for a vote row whose balance id is gone: the expected one first, then
// the nearest ones within the backend tolerance, earlier before later.
const getUnlockSecondsCandidates = (expectedUnlockTimestamp: number): number[] => {
    const expectedSeconds = Math.ceil(expectedUnlockTimestamp / 1000);
    const candidates = [expectedSeconds];

    for (let offset = 1; offset <= UNLOCK_TIMESTAMP_TOLERANCE_SECONDS; offset++) {
        candidates.push(expectedSeconds - offset, expectedSeconds + offset);
    }

    return candidates;
};

export const groupProposalVoteClaims = <T extends VoteClaimBalance>(
    proposal: Pick<ProposalSimple, 'end_at' | 'logvote_set'>,
    balances: T[],
    reissuedAssetCodes: string[],
    accountId: string,
): { groups: VoteClaimGroup<T>[]; rows: VoteClaimRow[] } => {
    const groups = new Map<string, VoteClaimGroup<T>>();
    const balanceKeys = new Map<string, string>();

    balances.forEach(balance => {
        const unlockTimestamp = new Date(balance.claimBackDate).getTime();
        const key = getVoteClaimGroupKey({
            voteChoice: balance.voteChoice,
            assetCode: balance.assetCode,
            balanceId: balance.id,
            unlockTimestamp,
            reissuedAssetCodes,
        });
        balanceKeys.set(balance.id, key);

        const group = groups.get(key);

        if (!group) {
            groups.set(key, {
                key,
                voteChoice: balance.voteChoice,
                assetCode: balance.assetCode,
                balances: [balance],
                logs: [],
                amount: new BigNumber(balance.amount).toFixed(7),
                unlockTimestamp,
            });
            return;
        }

        if (group.balances.some(({ id }) => id === balance.id)) {
            return;
        }

        group.balances.push(balance);
        group.amount = new BigNumber(group.amount).plus(balance.amount).toFixed(7);
        group.unlockTimestamp = Math.min(group.unlockTimestamp, unlockTimestamp);
    });

    const expectedUnlockTimestamp = proposal.end_at
        ? getGovernanceVoteUnlockTimestamp(proposal.end_at)
        : null;

    const getMissingBalanceGroupKey = (log: LogVote): string => {
        const expectedKey = getVoteClaimGroupKey({
            voteChoice: log.vote_choice,
            assetCode: log.asset_code,
            balanceId: log.claimable_balance_id,
            unlockTimestamp: expectedUnlockTimestamp,
            reissuedAssetCodes,
        });

        if (!reissuedAssetCodes.includes(log.asset_code) || expectedUnlockTimestamp === null) {
            return expectedKey;
        }

        const matchingKey = getUnlockSecondsCandidates(expectedUnlockTimestamp)
            .map(seconds =>
                getVoteClaimGroupKey({
                    voteChoice: log.vote_choice,
                    assetCode: log.asset_code,
                    balanceId: log.claimable_balance_id,
                    unlockTimestamp: seconds * 1000,
                    reissuedAssetCodes,
                }),
            )
            .find(key => groups.has(key));

        return matchingKey ?? expectedKey;
    };

    const rows = proposal.logvote_set
        .filter(log => log.account_issuer === accountId)
        .map(log => {
            const key = balanceKeys.get(log.claimable_balance_id) ?? getMissingBalanceGroupKey(log);

            const group = groups.get(key);

            if (group) {
                group.logs.push(log);
            } else {
                groups.set(key, {
                    key,
                    voteChoice: log.vote_choice,
                    assetCode: log.asset_code,
                    balances: [],
                    logs: [log],
                    amount: '0.0000000',
                    unlockTimestamp: expectedUnlockTimestamp,
                });
            }

            return { log, groupKey: key };
        });

    return { groups: Array.from(groups.values()), rows };
};

export const getVoteClaimStatus = (group: VoteClaimGroup, now = Date.now()): VoteClaimStatus => {
    if (!group.balances.length) {
        return VoteClaimStatus.claimed;
    }

    if (!Number.isFinite(group.unlockTimestamp) || group.unlockTimestamp > now) {
        return VoteClaimStatus.locked;
    }

    return VoteClaimStatus.claimable;
};

// Returns the live balances of the given claimable groups, each balance once.
export const getVoteClaimBalances = <T extends VoteClaimBalance>(
    groups: VoteClaimGroup<T>[],
    keys: Iterable<string>,
    now = Date.now(),
): T[] => {
    const keySet = new Set(keys);
    const balances = new Map<string, T>();

    groups.forEach(group => {
        if (
            !keySet.has(group.key) ||
            getVoteClaimStatus(group, now) !== VoteClaimStatus.claimable
        ) {
            return;
        }

        group.balances.forEach(balance => {
            balances.set(balance.id, balance);
        });
    });

    return Array.from(balances.values());
};

// ICE assets among the balances to claim, in the order of `iceAssetCodes`; each of them needs
// the ICE approval flow.
export const getVoteClaimIceAssetCodes = (
    balances: Pick<VoteClaimBalance, 'assetCode'>[],
    iceAssetCodes: string[],
): string[] => iceAssetCodes.filter(code => balances.some(({ assetCode }) => assetCode === code));

export const getMaxVoteClaimOperations = (iceAssetCount: number): number =>
    MAX_OPERATIONS_PER_TRANSACTION - ICE_APPROVAL_OPERATIONS_PER_ASSET * iceAssetCount;

// Every balance is claimed by one operation.
export const isVoteClaimOverOperationLimit = (
    balances: Pick<VoteClaimBalance, 'assetCode'>[],
    iceAssetCodes: string[],
): boolean =>
    balances.length >
    getMaxVoteClaimOperations(getVoteClaimIceAssetCodes(balances, iceAssetCodes).length);
